import { EditIcon } from '@chakra-ui/icons'
import { Badge, Box, BoxProps, Flex, HStack, IconButton, Text, Tooltip } from '@chakra-ui/react'
import { useApp, useAuth, useCollectionItem } from '@hb/react'
import {
  BaseInsuranceCoverage,
  capitalizeFirstLetter,
  colors,
  CoverageSnippet,
  defaultStageValidate,
  FieldMapValue,
  getCoverageLabel,
  getCoverageNextActionEntity,
  getCoverageText,
  getDateString,
  InsuranceCoverage,
  InsuranceCoverageUpdateType,
  insurersCollection,
  makeAllFieldsOptional,
  OnUploadProgress,
  PatientCoverageId,
  UpdateCallback,
  xor,
} from '@hb/shared'
import { set as nestedSet } from 'nested-property'
import { JSX, useCallback, useContext, useMemo, useState } from 'react'
import { processFieldMapData } from '../../../backend/db/utils/processData'
import { useAppGroup } from '../../../hooks/backend/user/useAppGroup'
import { AddItem } from '../../AddItem'
import { DataView, GenericEditModal } from '../../DataView'
import { Expandable } from '../../Expandable'
import { SimpleForm } from '../../forms/FinalForm/SimpleForm'
import { DefaultModal } from '../../Modals/DefaultModal'
import {
  EntityNextActionContent,
  useSubmitCoverageNextAction,
} from '../../Users/Profile/NextAction'
import { EligibilityRequestPopover } from '../../Users/Profile/Patient/EligibilityRequest'
import { CoverageViewContext, CoverageViewProvider, useCoverageView } from './context'
import { CoverageActions } from './CoverageActions'
import { CoverageHistory } from './CoverageHistory'
import { CoverageStatusBadge } from './CoverageStatusBadge'
import { RequestCallIn } from './RequestCallIn'
import { CoverageStageProps } from './types'
import { coverageSnippetStage } from './utils'

const coverageSnippetUpdateType: Record<CoverageSnippet, InsuranceCoverageUpdateType> = {
  'basic-info': 'basicInfo',
  'call-in': 'callIn',
  'policy-owner': 'policyOwner',
}
const formBoxProps: BoxProps = {
  py: 2,
  px: 1,
  boxShadow: 'none',
}

const CoverageSnippetView = ({
  id,
  coverageId,
  editDisabled,
  inModal,
}: {
  id: CoverageSnippet
  coverageId: PatientCoverageId
  editDisabled?: boolean
  inModal?: boolean
}) => {
  const authUser = useAuth(s => s.authUser)
  const { coverage, handleSubmit, adminView, baseStoragePath } = useContext(CoverageViewContext)
  const [isEditing, setIsEditing] = useState(false)
  const { isMedicaid } = coverage ?? {}
  const { appName } = useApp()
  const { admin } = useAuth()
  const field = useMemo(() => {
    const f = coverageSnippetStage[id](!!isMedicaid)
    return adminView ? makeAllFieldsOptional(f) : f
  }, [id, isMedicaid, adminView])

  const group = useAppGroup()
  const isComplete = useMemo(() => !defaultStageValidate(field, true)(coverage), [field, coverage])

  const onUpdateField = useCallback(
    async (path: string, data: FieldMapValue): Promise<UpdateCallback> => {
      const uid = authUser?.uid
      if (!uid) return { error: 'Not signed in' }
      if (!coverage && coverageId.startsWith('.additionalPlans'))
        return { error: 'No coverage to update' }
      let submitted: Partial<InsuranceCoverage> = {}
      if (path) {
        nestedSet(submitted, path, data)
      } else submitted = data
      submitted.history = [
        ...(coverage?.history && Array.isArray(coverage?.history) ? coverage.history : []),
        { by: uid, on: Date.now(), type: coverageSnippetUpdateType[id], group },
      ]
      return handleSubmit(submitted).then(errors => {
        if (errors) return { error: 'An error occurred' }
        return { success: 'Updated!' }
      })
    },
    [coverage, handleSubmit, authUser, id, coverageId, group],
  )

  const updateField = useMemo(
    () => (editDisabled ? undefined : onUpdateField),
    [editDisabled, onUpdateField],
  )

  const onSubmit = useCallback(
    async (data: FieldMapValue, onUploadProgress: OnUploadProgress) => {
      const uid = authUser?.uid
      if (!uid) return { error: 'Not logged in' }
      const processed = await processFieldMapData(
        appName,
        admin,
        uid,
        baseStoragePath,
        field,
        data,
        coverage,
        onUploadProgress,
        null,
        true,
      )
      return handleSubmit(processed)
    },
    [handleSubmit, baseStoragePath, field, coverage, authUser, appName, admin],
  )

  let body: JSX.Element | null = null
  if (adminView || isComplete || editDisabled) {
    body = (
      <DataView
        boxProps={{
          p: 0,
          boxShadow: 'none',
          borderBottom: '1px solid #cdcdcd',
        }}
        adminView={!!adminView}
        onSubmit={editDisabled ? undefined : onSubmit}
        noHeader
        baseStoragePath={baseStoragePath}
        updateField={editDisabled ? undefined : updateField}
        data={coverage}
        field={field}
      />
    )
  } else {
    body = <SimpleForm boxProps={formBoxProps} onSubmit={onSubmit} value={coverage} field={field} />
  }
  return (
    <>
      <Expandable
        borderBottom="1px solid #cdcdcd"
        initExpanded={!isComplete}
        headerProps={{ bg: '#e4f6ef', _hover: { bg: 'green.100' } }}
        bg="white"
        header={({ isOpen }) => (
          <HStack flex={1} pl={2} py={1} minW="0" spacing={0}>
            <Flex color="#565656" position="relative" fontWeight={600} fontFamily="Open Sans">
              {field.name}
            </Flex>
            <Flex gap={2} ml="auto">
              {adminView && id === 'call-in' && coverageId.startsWith('additional.') ? (
                <RequestCallIn visible={isOpen} />
              ) : null}
              {adminView || (id !== 'call-in' && !editDisabled) ? (
                <Tooltip label={editDisabled ? 'Please complete previous steps' : ''}>
                  <IconButton
                    aria-label="edit"
                    pointerEvents={editDisabled ? 'none' : 'auto'}
                    color={editDisabled ? 'gray.400' : 'gray.600'}
                    icon={<EditIcon />}
                    _hover={{ bg: 'whiteAlpha.500' }}
                    h={7}
                    w={7}
                    minW={0}
                    borderRadius="full"
                    size="sm"
                    variant="ghost"
                    onClick={e => {
                      e.stopPropagation()
                      if (editDisabled) return
                      setIsEditing(true)
                    }}
                  />
                </Tooltip>
              ) : null}
            </Flex>
          </HStack>
        )}>
        <Flex bg="white" px={inModal ? 2 : 0} w="100%">
          {body}
        </Flex>
      </Expandable>
      {isEditing ? (
        <GenericEditModal
          isOpen={isEditing}
          onClose={() => setIsEditing(false)}
          onSubmit={data => onUpdateField('', data)}
          field={field}
          data={coverage}
        />
      ) : null}
    </>
  )
}
const CoverageTimeline = ({
  coverage,
}: {
  coverage: InsuranceCoverage | BaseInsuranceCoverage | undefined | null
}) => {
  const {
    terminationDate,
    initialServiceDate: nonMedicaidStartDate,
    isMedicaid,
    medicaidStartDate,
  } = coverage ?? {}
  const initialServiceDate = useMemo(() => {
    if (isMedicaid) return medicaidStartDate
    return nonMedicaidStartDate
  }, [isMedicaid, medicaidStartDate, nonMedicaidStartDate])
  const hasTerminated = useMemo(() => {
    if (!terminationDate) return false
    const now = Date.now()
    return terminationDate < now
  }, [terminationDate])
  const statusColor = useMemo(() => {
    const now = Date.now()
    if (hasTerminated) return 'red'
    if (initialServiceDate && initialServiceDate > now) return 'gray'
    return 'green'
  }, [initialServiceDate, hasTerminated])

  const formattedCoverageStartDate = useMemo(
    () => (initialServiceDate ? getDateString(initialServiceDate, 'short') : ''),
    [initialServiceDate],
  )

  const formattedTerminationDate = useMemo(
    () => (terminationDate ? getDateString(terminationDate, 'short') : ''),
    [terminationDate],
  )

  const text = useMemo(() => {
    if (formattedCoverageStartDate && formattedTerminationDate) {
      return `${formattedCoverageStartDate} - ${formattedTerminationDate}`
    }
    if (formattedCoverageStartDate) {
      const hasStarted = initialServiceDate && initialServiceDate < Date.now()
      return `${hasStarted ? 'Started' : 'Starts'} ${formattedCoverageStartDate}`
    }
    if (formattedTerminationDate) {
      return `${hasTerminated ? 'Terminated' : 'Terminates'} ${formattedTerminationDate}`
    }
    return 'No start/end date'
  }, [hasTerminated, formattedCoverageStartDate, formattedTerminationDate, initialServiceDate])

  return (
    <Flex>
      <Flex gap={3} borderRadius={4} px={2} align="center" bg={`${statusColor}.100`}>
        <Text whiteSpace="nowrap" fontWeight={600} fontSize="xs" color={`${statusColor}.700`}>
          {text.toUpperCase()}
        </Text>
      </Flex>
    </Flex>
  )
}

const CoverageNextAction = () => {
  const { coverageId: id, coverage, assessmentId, nextAction } = useCoverageView()
  const nextActionEntity = useMemo(() => getCoverageNextActionEntity(id, coverage), [id, coverage])
  if (!assessmentId)
    return (
      <Text fontSize="sm" color="gray.500" fontWeight={600} px={2} py={1}>
        Select a pregnancy to view/edit next actions
      </Text>
    )
  return (
    <EntityNextActionContent
      inList={false}
      entity={nextActionEntity}
      assessmentId={assessmentId}
      nextAction={nextAction}
    />
  )
}

const CoveragePreview = ({
  mobileLayout,
  adminView,
}: {
  mobileLayout: boolean
  adminView?: boolean
}) => {
  const {
    coverageId: id,
    coverage,
    nextAction,
    assessment,
    assessmentId,
    coverageStatus: status,
  } = useCoverageView()
  const coverageName = useMemo(
    () => capitalizeFirstLetter(getCoverageLabel(id, assessmentId ? assessment : undefined)),
    [id, assessment, assessmentId],
  )
  const { item: insuranceProvider } = useCollectionItem(
    insurersCollection,
    coverage?.insuranceProviderId,
  )

  const coverageText = useMemo(
    () => (coverage ? getCoverageText(coverage, insuranceProvider, true) : ''),
    [coverage, insuranceProvider],
  )
  const hasEligibilityRequest = useMemo(
    () => Object.keys(coverage?.eligibilityRequests ?? {}).length,
    [coverage],
  )
  return (
    <Flex flexFlow="column" w="100%">
      <Flex
        flexFlow={mobileLayout ? 'column' : 'row'}
        py={1}
        gap={mobileLayout ? 0 : 2}
        align={mobileLayout ? 'center' : 'flex-start'}
        w="100%">
        <Flex gap={1} px={2} w="100%" align="center" flexFlow="row">
          <Flex gap={2} flex={1} minW="0" align="center">
            <Text
              minW="0"
              color="white"
              isTruncated
              fontSize="lg"
              textShadow="1px 1px 3px #00000066"
              fontWeight={600}>
              {coverageName} Coverage - {coverageText}
            </Text>
            {coverage?.isMedicaid ? <Badge>Medicaid</Badge> : null}
          </Flex>
          <Flex gap={1.5} align="center" ml="auto" flexFlow="row">
            {mobileLayout ? null : (
              <>
                <CoverageStatusBadge status={status} />
                <CoverageTimeline coverage={coverage} />
              </>
            )}
            {coverage ? <CoverageHistory coverage={coverage} /> : null}
          </Flex>
        </Flex>
        {mobileLayout ? (
          <Flex
            pt={1}
            px={2}
            borderTop="1px solid #00000022"
            w="100%"
            justify="space-between"
            align="center">
            <CoverageStatusBadge status={status} />
            <CoverageTimeline coverage={coverage} />
          </Flex>
        ) : null}
      </Flex>
      {adminView && nextAction ? <CoverageNextAction /> : null}
      {adminView && hasEligibilityRequest ? <SubmitCoverageEligibilityRequest /> : null}
    </Flex>
  )
}

const AddNextAction = ({
  assessmentId,
  id,
}: {
  id: PatientCoverageId
  assessmentId: string | null
}) => {
  const onSubmit = useSubmitCoverageNextAction(assessmentId, id, false)
  return (
    <Flex borderBottom="1px solid #cdcdcd" align="center" w="100%" bg="gray.100" pl={3}>
      <Text opacity={0.8} fontWeight={600}>
        Next Action
      </Text>
      <Flex ml="auto">
        {assessmentId ? (
          <AddItem
            placeholder="Next action"
            itemName="Next Action"
            onAdd={text => onSubmit(text)}
          />
        ) : (
          <Flex px={2} py={1.5}>
            <Text fontSize="sm" opacity={0.7}>
              Select a pregnancy to view/edit next action
            </Text>
          </Flex>
        )}
      </Flex>
    </Flex>
  )
}

const SubmitCoverageEligibilityRequest = () => {
  const { coverageId } = useCoverageView()
  return (
    <Flex bg="gray.100" borderBottom="1px solid #cdcdcd" w="100%" py={1} px={2}>
      <Flex flex={1} minW="0">
        <Text isTruncated opacity={0.8} fontWeight={600}>
          Eligibility Requests
        </Text>
      </Flex>
      <EligibilityRequestPopover coverageId={coverageId} />
    </Flex>
  )
}

const CoverageStageBody = <T extends InsuranceCoverage>({
  request,
  nextAction,
  adminView,
  inModal,
}: CoverageStageProps<T>) => {
  const {
    coverageId: id,
    assessmentId,
    assessment,
    coverage,
    hasCallIn,
    coverageStatus: status,
  } = useCoverageView()
  const { appName } = useApp()

  const { isMedicaid } = coverage ?? {}
  const basicInfoComplete = useMemo(() => !status?.incomplete.includes('basic-info'), [status])

  const showRequestCallIn = useMemo(() => !!adminView && !isMedicaid, [adminView, isMedicaid])
  const hasEligibilityRequest = useMemo(
    () => Object.keys(coverage?.eligibilityRequests ?? {}).length,
    [coverage],
  )

  const canEditBasicInfo = useMemo(() => {
    if (adminView) return true
    if (!assessmentId) return false
    return !assessment?.submittedOn
  }, [adminView, assessment, assessmentId])

  const canEditCallIn = useMemo(() => {
    if (adminView) return true
    if (!assessmentId) return false
    return basicInfoComplete && !assessment?.submittedOn
  }, [adminView, assessment, basicInfoComplete, assessmentId])

  const canEditPolicyOwner = useMemo(() => {
    if (appName === 'providers-app') return false
    if (adminView) return true
    if (!assessmentId) return false
    return appName === 'app' || !assessment?.submittedOn
  }, [adminView, assessmentId, appName, assessment])

  const showCoverageActions = useMemo(
    () => adminView && coverage && appName === 'app',
    [adminView, appName, coverage],
  )
  return (
    <>
      {adminView && !nextAction ? <AddNextAction assessmentId={assessmentId} id={id} /> : null}
      {adminView && !hasEligibilityRequest ? <SubmitCoverageEligibilityRequest /> : null}
      <Flex w="100%" bg="gray.50">
        <CoverageSnippetView
          coverageId={id}
          id="basic-info"
          editDisabled={!canEditBasicInfo}
          inModal={inModal}
        />
      </Flex>
      {(hasCallIn || showRequestCallIn) && !request ? (
        <CoverageSnippetView coverageId={id} editDisabled={!canEditCallIn} id="call-in" />
      ) : null}
      {request ? null : (
        <CoverageSnippetView coverageId={id} id="policy-owner" editDisabled={!canEditPolicyOwner} />
      )}
      {showCoverageActions ? <CoverageActions adminView={adminView} /> : null}
    </>
  )
}

const CoverageStageContent = <T extends InsuranceCoverage>(props: CoverageStageProps<T>) => {
  const { isOpen, onOpenToggle, adminView, width, inModal, alwaysOpen } = props

  const { complete } = useCoverageView()
  const initExpanded = useMemo(() => xor(!complete, !!adminView), [complete, adminView])

  const mobileLayout = useMemo(() => width < 769, [width])
  return (
    <Expandable
      isOpen={isOpen}
      alwaysExpanded={inModal || alwaysOpen}
      onClose={onOpenToggle}
      onOpen={onOpenToggle}
      bg={inModal ? 'transparent' : 'gray.50'}
      initExpanded={initExpanded}
      overflow="hidden"
      border="1px solid #cdcdcd"
      borderRadius={inModal ? 0 : 6}
      iconColor="whiteAlpha.900"
      headerProps={{
        bg: colors.green.hex,
        pl: 0,
        pr: 2,
        py: 0,
      }}
      header={() => (
        <Box
          w="100%"
          mr={alwaysOpen ? 0 : 2}
          borderRight={alwaysOpen ? 'none' : '1px solid #00000033'}>
          <CoveragePreview mobileLayout={mobileLayout} adminView={adminView} />
        </Box>
      )}>
      <CoverageStageBody {...props} />
    </Expandable>
  )
}

export const CoverageStage = <T extends InsuranceCoverage>(props: CoverageStageProps<T>) => (
  <CoverageViewProvider {...props}>
    <CoverageStageContent {...props} />
  </CoverageViewProvider>
)

export const CoverageStageModal = <T extends InsuranceCoverage>({
  isOpen,
  onClose,
  ...props
}: CoverageStageProps<T> & { isOpen: boolean; onClose: () => void }) => (
  <DefaultModal
    size="xl"
    isOpen={isOpen}
    onClose={onClose}
    overlayHeader
    render={() => <CoverageStage inModal isOpen {...props} />}
  />
)
